import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  Button,
  DataTable,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  DateInput,
  EmptyState,
  FormField,
  NumberInput,
  PageHeader,
  SectionPanel,
  Select,
  TableToolbar,
  TextInput,
} from "../../components/ui";
import NieuweWerkbonModal from "../NieuweWerkbonModal";
import ProjectDossierModal from "../../components/projectDossier/ProjectDossierModal";
import {
  downloadProjectExportJob,
  getProjectExportJob,
  listProjectExportJobs,
  startProjectExportJob,
} from "../../services/projectExportJobs";
import {
  getProjectImportJob,
  listProjectImportJobs,
  previewProjectImportPackage,
  startProjectImportJob,
} from "../../services/projectImportJobs";
import {
  getApiErrorMessage,
  getApiFieldErrors,
  hasApiErrorCode,
  showApiErrorToast,
} from "../../utils/apiError";

const PROJECT_STATUS_OPTIONS = [
  { value: "nieuw", label: "Nieuw" },
  { value: "actief", label: "Actief" },
  { value: "on_hold", label: "On hold" },
  { value: "afgerond", label: "Afgerond" },
  { value: "gearchiveerd", label: "Gearchiveerd" },
];

export default function ProjectenPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [opnames, setOpnames] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [calculaties, setCalculaties] = useState([]);
  const [attentionThreads, setAttentionThreads] = useState([]);
  const [attentionCountOverrides, setAttentionCountOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rowActionId, setRowActionId] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [editingProjectId, setEditingProjectId] = useState("");
  const [showNieuweWerkbon, setShowNieuweWerkbon] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [dossierProject, setDossierProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [exportingProjectId, setExportingProjectId] = useState("");
  const [exportJobsByProjectId, setExportJobsByProjectId] = useState({});
  const pollersRef = useRef({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const importPollerRef = useRef(null);

  const [form, setForm] = useState({
    naam: "",
    projectNummer: "",
    clientId: "",
    omschrijving: "",
    locationText: "",
    startDate: "",
    typeWerk: "projectwerk",
    facturatieModel: "nacalculatie",
    facturatieFrequentie: "maandelijks",
    betaalTermijnDagen: "30",
    budgetHours: "",
    budgetRevenueExcl: "",
    budgetMaterialsExcl: "",
    contactPersonId: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const communicationAttentionByProjectId = useMemo(() => {
    const counts = {};
    for (const thread of attentionThreads) {
      const projectId = String(thread?.projectId?._id || thread?.projectId || "").trim();
      if (!projectId) continue;
      counts[projectId] = Number(counts[projectId] || 0) + 1;
    }
    return {
      ...counts,
      ...attentionCountOverrides,
    };
  }, [attentionCountOverrides, attentionThreads]);

  useEffect(() => {
    return () => {
      Object.values(pollersRef.current).forEach((timerId) => {
        window.clearInterval(timerId);
      });
      pollersRef.current = {};
      if (importPollerRef.current) {
        window.clearInterval(importPollerRef.current);
        importPollerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!form.clientId) {
      setForm((prev) => ({ ...prev, contactPersonId: "" }));
      return;
    }

    const selectedClient = clients.find((client) => String(client._id) === String(form.clientId));
    const contactExists = Array.isArray(selectedClient?.contactPersons)
      && selectedClient.contactPersons.some(
        (contact) => String(contact?._id || "") === String(form.contactPersonId)
      );

    if (!contactExists && form.contactPersonId) {
      setForm((prev) => ({ ...prev, contactPersonId: "" }));
    }
  }, [clients, form.clientId, form.contactPersonId]);

  async function loadData() {
    try {
      setLoading(true);
      setError("");

      const [projectRes, clientRes, werkbonRes, taskRes, calculatieRes, opnameRes, communicationRes] = await Promise.allSettled([
        api.get("/projects"),
        api.get("/clients"),
        api.get("/werkbonnen"),
        api.get("/tasks"),
        api.get("/calculaties"),
        api.get("/opnames"),
        api.get("/communications/threads", {
          params: {
            context: "project",
            needsInternalAttention: true,
          },
        }),
      ]);

      const nextProjects = projectRes.status === "fulfilled" && Array.isArray(projectRes.value?.data)
        ? projectRes.value.data
        : [];
      const nextClients = clientRes.status === "fulfilled" && Array.isArray(clientRes.value?.data)
        ? clientRes.value.data
        : [];
      const nextWerkbonnen = werkbonRes.status === "fulfilled" && Array.isArray(werkbonRes.value?.data)
        ? werkbonRes.value.data
        : [];
      const nextTasks = taskRes.status === "fulfilled" && Array.isArray(taskRes.value?.data)
        ? taskRes.value.data
        : [];
      const nextCalculaties = calculatieRes.status === "fulfilled" && Array.isArray(calculatieRes.value?.data)
        ? calculatieRes.value.data
        : [];
      const nextOpnames = opnameRes.status === "fulfilled" && Array.isArray(opnameRes.value?.data)
        ? opnameRes.value.data
        : [];
      const nextAttentionThreads =
        communicationRes.status === "fulfilled" && Array.isArray(communicationRes.value?.data?.items)
          ? communicationRes.value.data.items
          : [];

      setProjects(nextProjects);
      setClients(nextClients);
      setWerkbonnen(nextWerkbonnen);
      setTasks(nextTasks);
      setCalculaties(nextCalculaties);
      setOpnames(nextOpnames);
      setAttentionThreads(nextAttentionThreads);
      setAttentionCountOverrides({});
    } catch (err) {
      setError(getApiErrorMessage(err, "Projecten laden mislukt"));
    } finally {
      setLoading(false);
    }
  }

  function handleCommunicationAttentionCountChange(projectId, nextCount) {
    const safeProjectId = String(projectId || "").trim();
    if (!safeProjectId) return;
    setAttentionCountOverrides((current) => {
      const resolvedCount = Math.max(0, Number(nextCount || 0));
      if (resolvedCount === 0 && current[safeProjectId] === undefined) {
        return current;
      }
      if (current[safeProjectId] === resolvedCount) {
        return current;
      }
      return {
        ...current,
        [safeProjectId]: resolvedCount,
      };
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.naam.trim()) return;

    try {
      setSaving(true);
      setError("");
      setFieldErrors({});

      const payload = {
        naam: form.naam.trim(),
        projectNummer: form.projectNummer.trim(),
        clientId: form.clientId || undefined,
        omschrijving: form.omschrijving.trim(),
        locationText: form.locationText.trim(),
        startDate: form.startDate || undefined,
        typeWerk: form.typeWerk,
        facturatieModel: form.facturatieModel,
        facturatieFrequentie: form.facturatieFrequentie,
        betaalTermijnDagen: form.betaalTermijnDagen || 30,
        budgetHours: form.budgetHours || 0,
        budgetRevenueExcl: form.budgetRevenueExcl || 0,
        budgetMaterialsExcl: form.budgetMaterialsExcl || 0,
        contactPersonId: form.contactPersonId || undefined,
      };

      if (editingProjectId) {
        await api.put(`/projects/${editingProjectId}`, payload);
      } else {
        await api.post("/projects", payload);
      }

      resetForm();
      await loadData();
    } catch (err) {
      const nextFieldErrors = getApiFieldErrors(err);
      setFieldErrors(nextFieldErrors);
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setError("Controleer de invoer en probeer opnieuw.");
      } else {
        setError(
          getApiErrorMessage(
            err,
            editingProjectId ? "Project bijwerken mislukt" : "Project aanmaken mislukt"
          )
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setEditingProjectId("");
    setFieldErrors({});
    setForm({
      naam: "",
      projectNummer: "",
      clientId: "",
      omschrijving: "",
      locationText: "",
      startDate: "",
      typeWerk: "projectwerk",
      facturatieModel: "nacalculatie",
      facturatieFrequentie: "maandelijks",
      betaalTermijnDagen: "30",
      budgetHours: "",
      budgetRevenueExcl: "",
      budgetMaterialsExcl: "",
      contactPersonId: "",
    });
  }

  function openNieuweWerkbonForProject(project) {
    setSelectedProject(project);
    setShowNieuweWerkbon(true);
  }

  function openProjectDossier(project) {
    setDossierProject(project);
  }

  function startEditProject(project) {
    setEditingProjectId(String(project._id));
    setForm({
      naam: project.naam || project.title || "",
      projectNummer: project.projectNummer || project.number || "",
      clientId: String(project.clientId?._id || project.clientId || ""),
      omschrijving: project.omschrijving || "",
      locationText: project.locationText || "",
      startDate: toDateInputValue(project.startDate),
      typeWerk: project.typeWerk || "projectwerk",
      facturatieModel: project.facturatieModel || "nacalculatie",
      facturatieFrequentie: project.facturatieFrequentie || "maandelijks",
      betaalTermijnDagen: toNumberInputValue(project.betaalTermijnDagen || 30) || "30",
      budgetHours: toNumberInputValue(project.budgetHours),
      budgetRevenueExcl: toNumberInputValue(project.budgetRevenueExcl),
      budgetMaterialsExcl: toNumberInputValue(project.budgetMaterialsExcl),
      contactPersonId: String(project.contactPersonId || ""),
    });
    setError("");
    setFieldErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function updateProjectStatus(project, status) {
    try {
      setRowActionId(`status-${project._id}`);
      setError("");
      await api.put(`/projects/${project._id}`, { status });
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Projectstatus bijwerken mislukt"));
    } finally {
      setRowActionId("");
    }
  }

  async function deleteProject(project) {
    const confirmed = window.confirm(
      `Project "${project.naam || project.title || project.projectNummer || "onbekend"}" verwijderen?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setRowActionId(`delete-${project._id}`);
      setError("");
      await api.delete(`/projects/${project._id}`);

      if (editingProjectId === String(project._id)) {
        resetForm();
      }

      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Project verwijderen mislukt"));
    } finally {
      setRowActionId("");
    }
  }

  async function createProjectTask(project, payload) {
    try {
      setSaving(true);
      setError("");
      await api.post("/tasks", {
        projectId: project._id,
        title: payload.title,
        prio: payload.prio || "normaal",
        status: "open",
        kind: "taak",
      });
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Taak aanmaken mislukt"));
    } finally {
      setSaving(false);
    }
  }

  async function updateProjectTaskStatus(task, status) {
    try {
      setSaving(true);
      setError("");
      await api.patch(`/tasks/${task._id}/status`, { status });
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Taakstatus bijwerken mislukt"));
    } finally {
      setSaving(false);
    }
  }

  async function saveProjectNotes(project, notes) {
    try {
      setSaving(true);
      setError("");
      await api.put(`/projects/${project._id}`, { omschrijving: notes });
      await loadData();
      setDossierProject((prev) => (prev ? { ...prev, omschrijving: notes } : prev));
    } catch (err) {
      setError(getApiErrorMessage(err, "Notities opslaan mislukt"));
    } finally {
      setSaving(false);
    }
  }

  async function uploadProjectDocument(project, { file, categorie }) {
    try {
      if (!file) return;
      setSaving(true);
      setError("");
      const formData = new FormData();
      formData.append("bestand", file);
      formData.append("categorie", categorie || "document");
      await api.post(`/projects/${project._id}/document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadData();
    } catch (err) {
      const normalized = showApiErrorToast(err, {
        fallbackMessage: "Projectdocument upload mislukt",
      });
      setError(normalized.message);
    } finally {
      setSaving(false);
    }
  }

  async function deleteProjectDocument(project, document) {
    const confirmed = window.confirm(`Document "${document?.naam || "onbekend"}" verwijderen?`);
    if (!confirmed) return;
    try {
      setSaving(true);
      setError("");
      await api.delete(`/projects/${project._id}/document/${document._id}`);
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Projectdocument verwijderen mislukt"));
    } finally {
      setSaving(false);
    }
  }

  const setProjectExportJob = useCallback((projectId, job) => {
    setExportJobsByProjectId((prev) => ({ ...prev, [projectId]: job || null }));
  }, []);

  const stopExportPolling = useCallback((projectId) => {
    const timerId = pollersRef.current[projectId];
    if (timerId) {
      window.clearInterval(timerId);
      delete pollersRef.current[projectId];
    }
  }, []);

  const startExportPolling = useCallback((projectId, jobId) => {
    stopExportPolling(projectId);
    const timerId = window.setInterval(async () => {
      try {
        const nextJob = await getProjectExportJob(projectId, jobId);
        if (!nextJob) return;
        setProjectExportJob(projectId, nextJob);
        const status = String(nextJob?.status || "");
        if (["done", "failed", "expired"].includes(status)) {
          stopExportPolling(projectId);
        }
      } catch (err) {
        stopExportPolling(projectId);
      }
    }, 3000);
    pollersRef.current[projectId] = timerId;
  }, [setProjectExportJob, stopExportPolling]);

  const refreshLatestProjectExportJob = useCallback(async (projectIdInput) => {
    const projectId = String(projectIdInput || "").trim();
    if (!projectId) return;
    try {
      const jobs = await listProjectExportJobs(projectId, 1);
      const latest = jobs[0] || null;
      setProjectExportJob(projectId, latest);
      if (latest && ["queued", "processing"].includes(String(latest.status || ""))) {
        startExportPolling(projectId, latest.id);
      }
    } catch (err) {
      // stilte: modal blijft bruikbaar zonder statushistorie
    }
  }, [setProjectExportJob, startExportPolling]);

  async function handlePrepareProjectDossierExport(project) {
    const projectId = String(project?._id || "").trim();
    if (!projectId) return;

    try {
      setExportingProjectId(projectId);
      setError("");
      const job = await startProjectExportJob(projectId);
      if (job) {
        setProjectExportJob(projectId, job);
        startExportPolling(projectId, job.id);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Export voorbereiden mislukt"));
    } finally {
      setExportingProjectId("");
    }
  }

  async function handleDownloadPreparedProjectDossier(project) {
    const projectId = String(project?._id || "").trim();
    if (!projectId) return;
    const job = exportJobsByProjectId[projectId];
    if (!job?.id || !job?.downloadAvailable) return;

    try {
      setExportingProjectId(projectId);
      setError("");
      await downloadProjectExportJob(projectId, job.id, job.fileName || "project-dossier.zip");
    } catch (err) {
      setError(getApiErrorMessage(err, "Downloaden van export mislukt"));
    } finally {
      setExportingProjectId("");
    }
  }

  function stopImportPolling() {
    if (importPollerRef.current) {
      window.clearInterval(importPollerRef.current);
      importPollerRef.current = null;
    }
  }

  function startImportPolling(jobId) {
    stopImportPolling();
    importPollerRef.current = window.setInterval(async () => {
      try {
        const next = await getProjectImportJob(jobId);
        if (!next) return;
        setImportJob(next);
        const status = String(next?.status || "");
        if (["done", "done_with_warnings", "failed", "expired"].includes(status)) {
          stopImportPolling();
          await loadData();
        }
      } catch (err) {
        stopImportPolling();
      }
    }, 3000);
  }

  async function refreshImportHistory() {
    try {
      const jobs = await listProjectImportJobs(8);
      setImportHistory(jobs);
    } catch {
      // stil
    }
  }

  async function handlePreviewImport() {
    if (!importFile) return;
    try {
      setImportBusy(true);
      setError("");
      const response = await previewProjectImportPackage(importFile);
      setImportPreview(response?.preview || null);
    } catch (err) {
      setError(getApiErrorMessage(err, "Importvoorstel voorbereiden mislukt"));
    } finally {
      setImportBusy(false);
    }
  }

  async function handleStartImport() {
    if (!importFile) return;
    try {
      setImportBusy(true);
      setError("");
      const mapping = importPreview?.suggestedMapping || null;
      const job = await startProjectImportJob(importFile, mapping);
      setImportJob(job);
      setImportPreview((prev) => prev || null);
      if (job?.id) {
        startImportPolling(job.id);
      }
      await refreshImportHistory();
    } catch (err) {
      setError(getApiErrorMessage(err, "Import starten mislukt"));
    } finally {
      setImportBusy(false);
    }
  }

  function resetImportModal() {
    stopImportPolling();
    setShowImportModal(false);
    setImportFile(null);
    setImportPreview(null);
    setImportJob(null);
    refreshImportHistory();
  }

  useEffect(() => {
    const projectId = String(dossierProject?._id || "").trim();
    if (!projectId) return;
    refreshLatestProjectExportJob(projectId);
  }, [dossierProject?._id, refreshLatestProjectExportJob]);

  useEffect(() => {
    if (!showImportModal) return;
    refreshImportHistory();
  }, [showImportModal]);

  const selectedClient = clients.find((client) => String(client._id) === String(form.clientId));
  const availableContacts = Array.isArray(selectedClient?.contactPersons)
    ? selectedClient.contactPersons.filter((contact) => contact?.active !== false)
    : [];
  const projectContactLabel = (project) => {
    const client = clients.find(
      (item) => String(item._id) === String(project?.clientId?._id || project?.clientId || "")
    );
    const contacts = Array.isArray(client?.contactPersons) ? client.contactPersons : [];
    const contact = contacts.find(
      (item) =>
        String(item?._id || "") === String(project?.contactPersonId || "") &&
        item?.active !== false
    );

    return [contact?.name, contact?.role, contact?.phone].filter(Boolean).join(" - ") || "-";
  };
  const projectWerkbonnen = (project) =>
    werkbonnen.filter(
      (werkbon) => String(werkbon?.projectId?._id || werkbon?.projectId || "") === String(project._id)
    );
  const filteredProjects = projects.filter((project) => {
    const haystack = [
      project.projectNummer,
      project.number,
      project.naam,
      project.title,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(searchTerm.trim().toLowerCase());
  });

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Projecten & planning"
        title="Projecten"
        description="Overzicht, projectaanmaak en dossieracties in een compacte beheerweergave."
        actions={<Button tone="secondary" onClick={() => setShowImportModal(true)}>Dossier importeren</Button>}
      />

      <SectionPanel
        title={editingProjectId ? "Project bewerken" : "Nieuw project"}
        description="Projectgegevens, klantkoppeling en begroting logisch gegroepeerd."
        actions={
          editingProjectId ? (
            <Button tone="secondary" size="sm" onClick={resetForm}>Annuleren</Button>
          ) : null
        }
      >
        <form onSubmit={handleCreate} className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Projectnaam" error={fieldErrors?.naam?.[0] || fieldErrors?.title?.[0]}>
              <TextInput
                value={form.naam}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, naam: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, naam: undefined, title: undefined }));
                }}
                placeholder="Projectnaam *"
                invalid={Boolean(fieldErrors?.naam || fieldErrors?.title)}
                required
              />
            </FormField>
            <FormField label="Projectnummer" error={fieldErrors?.projectNummer?.[0] || fieldErrors?.number?.[0]}>
              <TextInput
                value={form.projectNummer}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, projectNummer: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, projectNummer: undefined, number: undefined }));
                }}
                placeholder="Projectnummer"
                invalid={Boolean(fieldErrors?.projectNummer || fieldErrors?.number)}
              />
            </FormField>
            <FormField label="Klant">
              <Select
                value={form.clientId}
                onChange={(e) => setForm((prev) => ({ ...prev, clientId: e.target.value }))}
              >
                <option value="">Geen klant koppelen</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.naam}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Omschrijving">
              <TextInput
                value={form.omschrijving}
                onChange={(e) => setForm((prev) => ({ ...prev, omschrijving: e.target.value }))}
                placeholder="Omschrijving"
              />
            </FormField>
            <FormField label="Projectlocatie">
              <TextInput
                value={form.locationText}
                onChange={(e) => setForm((prev) => ({ ...prev, locationText: e.target.value }))}
                placeholder="Projectlocatie"
              />
            </FormField>
            <FormField label="Startdatum">
              <DateInput
                value={form.startDate}
                onChange={(e) => setForm((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </FormField>
            <FormField label="Type werk">
              <Select
                value={form.typeWerk}
                onChange={(e) => setForm((prev) => ({ ...prev, typeWerk: e.target.value }))}
              >
                <option value="projectwerk">projectwerk</option>
                <option value="regiewerk">regiewerk</option>
              </Select>
            </FormField>
            <FormField label="Facturatiemodel">
              <Select
                value={form.facturatieModel}
                onChange={(e) => setForm((prev) => ({ ...prev, facturatieModel: e.target.value }))}
              >
                <option value="nacalculatie">nacalculatie</option>
                <option value="termijn">termijn</option>
              </Select>
            </FormField>
            <FormField label="Factuurfrequentie">
              <Select
                value={form.facturatieFrequentie}
                onChange={(e) => setForm((prev) => ({ ...prev, facturatieFrequentie: e.target.value }))}
              >
                <option value="wekelijks">wekelijks</option>
                <option value="2-wekelijks">2-wekelijks</option>
                <option value="maandelijks">maandelijks</option>
              </Select>
            </FormField>
            <FormField label="Betaaltermijn (dagen)">
              <NumberInput
                min="0"
                step="1"
                value={form.betaalTermijnDagen}
                onChange={(e) => setForm((prev) => ({ ...prev, betaalTermijnDagen: e.target.value }))}
              />
            </FormField>
            <FormField label="Begrote uren">
              <NumberInput
                min="0"
                step="0.5"
                value={form.budgetHours}
                onChange={(e) => setForm((prev) => ({ ...prev, budgetHours: e.target.value }))}
              />
            </FormField>
            <FormField label="Begrote omzet excl">
              <NumberInput
                min="0"
                step="0.01"
                value={form.budgetRevenueExcl}
                onChange={(e) => setForm((prev) => ({ ...prev, budgetRevenueExcl: e.target.value }))}
              />
            </FormField>
            <FormField label="Begroot materiaal excl">
              <NumberInput
                min="0"
                step="0.01"
                value={form.budgetMaterialsExcl}
                onChange={(e) => setForm((prev) => ({ ...prev, budgetMaterialsExcl: e.target.value }))}
              />
            </FormField>
            <FormField label="Voorkeurscontact" className={form.clientId && availableContacts.length === 0 ? "md:col-span-2" : ""}>
              <Select
                value={form.contactPersonId}
                onChange={(e) => setForm((prev) => ({ ...prev, contactPersonId: e.target.value }))}
                disabled={!form.clientId}
              >
                <option value="">
                  {form.clientId ? "Geen voorkeurscontact" : "Kies eerst klant"}
                </option>
                {availableContacts.map((contact) => (
                  <option key={contact._id} value={contact._id}>
                    {[contact.name, contact.role].filter(Boolean).join(" - ") || "Contactpersoon"}
                  </option>
                ))}
              </Select>
            </FormField>
            {form.clientId && availableContacts.length === 0 ? (
              <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 md:col-span-2">
                Deze klant heeft nog geen contactpersonen. Voeg eerst een contactpersoon toe bij de
                klant, daarna kun je hier een voorkeurscontact kiezen.
                <div className="mt-2">
                  <Button tone="secondary" size="sm" onClick={() => navigate(`/klanten/${form.clientId}`)}>
                    Contactpersonen openen
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
          <Button type="submit" tone="primary" disabled={saving}>
            {saving ? "Opslaan..." : editingProjectId ? "Project bijwerken" : "Project opslaan"}
          </Button>
        </form>
      </SectionPanel>

      <SectionPanel title="Projectoverzicht" description="Scanbaar register van projecten, status en vervolgacties.">
        <TableToolbar>
          <div className="min-w-[240px] flex-1">
            <FormField label="Zoeken">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op projectnummer of naam"
              />
            </FormField>
          </div>
        </TableToolbar>
        {loading ? <p className="text-sm text-slate-500">Laden...</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {!loading && !error && filteredProjects.length === 0 ? (
          <EmptyState title="Nog geen projecten gevonden" />
        ) : null}

        {!loading && filteredProjects.length > 0 ? (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableCell header>Projectnr</DataTableCell>
                <DataTableCell header>Naam</DataTableCell>
                <DataTableCell header>Klant</DataTableCell>
                <DataTableCell header>Startdatum</DataTableCell>
                <DataTableCell header>Status</DataTableCell>
                <DataTableCell header className="text-right">Acties</DataTableCell>
              </DataTableRow>
            </DataTableHead>
            <tbody>
              {filteredProjects.map((p) => {
                const gekoppeldeWerkbonnen = projectWerkbonnen(p);

                return (
                  <FragmentRow
                    key={p._id}
                    project={p}
                    clients={clients}
                    gekoppeldeWerkbonnen={gekoppeldeWerkbonnen}
                    communicationAttentionCount={
                      Number(communicationAttentionByProjectId[String(p._id || "")] || 0)
                    }
                    rowActionId={rowActionId}
                    projectContactLabel={projectContactLabel}
                    onEdit={startEditProject}
                    onStatusChange={updateProjectStatus}
                    onDelete={deleteProject}
                    onCreateWerkbon={openNieuweWerkbonForProject}
                    onOpenDossier={openProjectDossier}
                  />
                );
              })}
            </tbody>
          </DataTable>
        ) : null}
      </SectionPanel>

      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-lg font-semibold">Projectdossier importeren</h3>
                <p className="text-xs text-slate-500">Upload een export-zip en controleer eerst het importvoorstel.</p>
              </div>
              <Button type="button" tone="secondary" size="sm" onClick={resetImportModal}>
                Sluiten
              </Button>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div className="rounded-lg border border-slate-200 p-3">
                <label className="mb-2 block text-sm font-medium text-slate-700">Dossierbestand (.zip)</label>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                  className="w-full rounded border border-slate-200 p-2 text-sm"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    tone="secondary"
                    size="sm"
                    onClick={handlePreviewImport}
                    disabled={importBusy || !importFile}
                  >
                    Voorcontrole starten
                  </Button>
                  <Button
                    type="button"
                    tone="primary"
                    size="sm"
                    onClick={handleStartImport}
                    disabled={importBusy || !importFile || (importPreview?.validation?.errors || []).length > 0}
                  >
                    Import uitvoeren
                  </Button>
                </div>
              </div>

              {importPreview && (
                <div className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-800">Importvoorstel</p>
                  <p className="mt-1 text-slate-600">
                    Werkbonnen: {Number(importPreview?.counts?.workorders || 0)} · Foto&apos;s: {Number(importPreview?.counts?.photos || 0)} ·
                    Bijlagen: {Number(importPreview?.counts?.attachments || 0)} · Projectdocumenten: {Number(importPreview?.counts?.projectDocuments || 0)}
                  </p>
                  <p className="mt-1 text-slate-600">
                    Checksum mismatches: {Number(importPreview?.counts?.checksumMismatches || 0)} · Ontbrekende bestanden: {Number(importPreview?.counts?.missingFiles || 0)}
                  </p>
                  <p className="mt-2 text-slate-600">
                    Klantmapping: <strong>{String(importPreview?.suggestedMapping?.client?.strategy || "onbekend")}</strong>
                    {importPreview?.suggestedMapping?.client?.reason ? ` - ${importPreview.suggestedMapping.client.reason}` : ""}
                  </p>
                  <p className="text-slate-600">
                    Projectmapping: <strong>{String(importPreview?.suggestedMapping?.project?.strategy || "onbekend")}</strong>
                    {importPreview?.suggestedMapping?.project?.reason ? ` - ${importPreview.suggestedMapping.project.reason}` : ""}
                  </p>
                  {(importPreview?.validation?.errors || []).length > 0 && (
                    <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-rose-700">
                      {(importPreview.validation.errors || []).map((item, idx) => (
                        <p key={`err-${idx}`}>• {item}</p>
                      ))}
                    </div>
                  )}
                  {(importPreview?.validation?.warnings || []).length > 0 && (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                      {(importPreview.validation.warnings || []).slice(0, 6).map((item, idx) => (
                        <p key={`warn-${idx}`}>• {item}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {importJob && (
                <div className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-800">Importstatus</p>
                  <p className="mt-1 text-slate-600">
                    {getImportStatusText(importJob)} ({Number(importJob?.progress || 0)}%)
                  </p>
                  {importJob?.errorMessage ? <p className="mt-1 text-rose-700">{importJob.errorMessage}</p> : null}
                  {importJob?.warningCount ? (
                    <p className="mt-1 text-amber-700">Aandachtspunten: {Number(importJob.warningCount)}</p>
                  ) : null}
                  {importJob?.result?.projectId ? (
                    <p className="mt-1 text-emerald-700">Nieuw project aangemaakt: {String(importJob.result.projectId)}</p>
                  ) : null}
                </div>
              )}

              {importHistory.length > 0 && (
                <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600">
                  <p className="font-medium text-slate-800">Recente imports</p>
                  <div className="mt-2 space-y-1">
                    {importHistory.map((job) => (
                      <p key={job.id}>
                        {String(job.sourceFileName || "zip")} · {String(job.status || "-")} · {String(job.createdAt || "").slice(0, 19).replace("T", " ")}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showNieuweWerkbon && selectedProject && (
        <NieuweWerkbonModal
          initialProjectId={selectedProject._id}
          initialClientId={String(selectedProject.clientId?._id || selectedProject.clientId || "")}
          onClose={() => {
            setShowNieuweWerkbon(false);
            setSelectedProject(null);
          }}
          onCreated={(wb) => {
            setShowNieuweWerkbon(false);
            setSelectedProject(null);
            if (wb?._id) {
              window.location.href = `/werkbon/${wb._id}`;
            }
          }}
        />
      )}

      {dossierProject && (
        <ProjectDossierModal
          open={Boolean(dossierProject)}
          project={dossierProject}
          client={clients.find((item) => String(item._id) === String(dossierProject.clientId?._id || dossierProject.clientId || "")) || null}
          workorders={projectWerkbonnen(dossierProject)}
          opnames={opnames.filter((item) => {
            const projectId = String(item?.project?._id || item?.projectId || "").trim();
            const projectNummer = String(item?.projectNummer || "").trim().toLowerCase();
            const currentProjectId = String(dossierProject._id || "").trim();
            const currentProjectNummer = String(
              dossierProject.projectNummer || dossierProject.number || ""
            )
              .trim()
              .toLowerCase();
            return (
              (projectId && projectId === currentProjectId) ||
              (projectNummer && currentProjectNummer && projectNummer === currentProjectNummer)
            );
          })}
          tasks={tasks.filter((item) => String(item.projectId?._id || item.projectId || "") === String(dossierProject._id))}
          calculaties={calculaties.filter((item) => String(item.projectId?._id || item.projectId || "") === String(dossierProject._id))}
          saving={saving}
          onClose={() => setDossierProject(null)}
          onCreateTask={(payload) => createProjectTask(dossierProject, payload)}
          onUpdateTaskStatus={updateProjectTaskStatus}
          onSaveNotes={(notes) => saveProjectNotes(dossierProject, notes)}
          onUploadProjectDocument={(payload) => uploadProjectDocument(dossierProject, payload)}
          onDeleteProjectDocument={(document) => deleteProjectDocument(dossierProject, document)}
          onPrepareDossierExport={() => handlePrepareProjectDossierExport(dossierProject)}
          onDownloadDossierExport={() => handleDownloadPreparedProjectDossier(dossierProject)}
          onDismissDossierExport={() => {
            const projectId = String(dossierProject?._id || "");
            if (!projectId) return;
            stopExportPolling(projectId);
            setProjectExportJob(projectId, null);
          }}
          dossierExportJob={exportJobsByProjectId[String(dossierProject._id || "")] || null}
          dossierExportBusy={exportingProjectId === String(dossierProject._id || "")}
          communicationAttentionCount={
            Number(communicationAttentionByProjectId[String(dossierProject._id || "")] || 0)
          }
          onCommunicationAttentionCountChange={(nextCount) =>
            handleCommunicationAttentionCountChange(dossierProject._id, nextCount)
          }
        />
      )}
    </div>
  );
}

function FragmentRow({
  project,
  clients,
  gekoppeldeWerkbonnen,
  communicationAttentionCount = 0,
  rowActionId,
  projectContactLabel,
  onEdit,
  onStatusChange,
  onDelete,
  onCreateWerkbon,
  onOpenDossier,
}) {
  const clientNaam = clients.find(
    (item) => String(item._id) === String(project.clientId?._id || project.clientId || "")
  )?.naam || "-";
  const workorderTotals = summarizeProjectWorkorders(gekoppeldeWerkbonnen);

  return (
    <>
      <tr className="border-b">
        <td className="px-3 py-2">{project.projectNummer || project.number || "-"}</td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span>{project.naam || project.title || "-"}</span>
            {communicationAttentionCount > 0 ? (
              <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                {communicationAttentionCount} klantreact{communicationAttentionCount === 1 ? "ie" : "ies"}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-2">{clientNaam}</td>
        <td className="px-3 py-2">{formatDate(project.startDate)}</td>
        <td className="px-3 py-2">
          <Select
            value={project.status || "actief"}
            onChange={(e) => onStatusChange(project, e.target.value)}
            disabled={rowActionId === `status-${project._id}`}
            className="h-8 text-xs"
          >
            {PROJECT_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            <Button tone="success" size="sm" onClick={() => onEdit(project)}>
              Bewerken
            </Button>
            <Button tone="primary" size="sm" onClick={() => onCreateWerkbon(project)}>
              Werkbon
            </Button>
            <Button tone="secondary" size="sm" onClick={() => onOpenDossier(project)}>
              Dossier{communicationAttentionCount > 0 ? ` (${communicationAttentionCount})` : ""}
            </Button>
            <Button
              onClick={() => onDelete(project)}
              disabled={rowActionId === `delete-${project._id}`}
              tone="danger"
              size="sm"
            >
              Verwijderen
            </Button>
          </div>
        </td>
      </tr>
      <tr className="border-b bg-gray-50">
        <td colSpan={6} className="px-3 py-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Locatie
              </div>
              <p className="mt-1 text-sm text-gray-700">{project.locationText || "-"}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Voorkeurscontact
              </div>
              <p className="mt-1 text-sm text-gray-700">{projectContactLabel(project)}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Begrote uren
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatBudget(project.budgetHours, "u")}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Geregistreerde uren
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatHours(workorderTotals.registeredHours)}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Type werk / facturatie
              </div>
              <p className="mt-1 text-sm text-gray-700">
                {(project.typeWerk || "projectwerk")} · {(project.facturatieModel || "nacalculatie")} · {(project.facturatieFrequentie || "maandelijks")}
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Begrote omzet excl
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatBudget(project.budgetRevenueExcl, "euro")}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Geboekte omzet excl
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatMoney(workorderTotals.revenueExcl)}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Begroot materiaal excl
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatBudget(project.budgetMaterialsExcl, "euro")}</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Geboekt materiaal excl
              </div>
              <p className="mt-1 text-sm text-gray-700">{formatMoney(workorderTotals.materialExcl)}</p>
            </div>
          </div>
          <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Werkbonnen op dit project
          </div>
          {gekoppeldeWerkbonnen.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Nog geen werkbonnen gekoppeld.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {gekoppeldeWerkbonnen.map((werkbon) => (
                <a
                  key={werkbon._id}
                  href={`/werkbon/${werkbon._id}`}
                  className="border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                >
                  {werkbon.werkbonNummer || werkbon.nummer || "Werkbon"} | {getWerkbonOmschrijving(werkbon)} | {werkbon.status || "-"} | {formatMoney(Number(werkbon?.financialTotals?.subtotalExcl || 0))}
                </a>
              ))}
            </div>
          )}
        </td>
      </tr>
    </>
  );
}

function getWerkbonOmschrijving(werkbon) {
  return werkbon?.omschrijving
    || werkbon?.opdracht?.omschrijving
    || werkbon?.scope?.samenvatting
    || "-";
}

function getImportStatusText(job) {
  const status = String(job?.status || "").toLowerCase();
  const step = String(job?.step || "").toLowerCase();

  if (status === "queued") return "Import voorbereid";
  if (status === "processing") {
    if (step === "validating") return "Dossier wordt gevalideerd";
    if (step === "mapping") return "Importmapping wordt bepaald";
    if (step === "importing") return "Gegevens worden geïmporteerd";
    if (step === "finalizing") return "Import wordt afgerond";
    return "Import wordt verwerkt";
  }
  if (status === "done") return "Import gereed";
  if (status === "done_with_warnings") return "Import gereed met aandachtspunten";
  if (status === "failed") return "Import mislukt";
  if (status === "expired") return "Import verlopen";
  return "Importstatus onbekend";
}

function toDateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function toNumberInputValue(value) {
  const numeric = Number(value || 0);
  return numeric > 0 ? String(numeric) : "";
}

function formatBudget(value, mode) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "-";
  if (mode === "u") return `${numeric.toLocaleString("nl-NL")} u`;
  return `€ ${numeric.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMoney(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "-";
  return `€ ${numeric.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatHours(value) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return "-";
  return `${numeric.toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} u`;
}

function summarizeProjectWorkorders(workorders) {
  return (Array.isArray(workorders) ? workorders : []).reduce(
    (acc, workorder) => {
      const hoursFromEntries = Array.isArray(workorder?.hourEntries)
        ? workorder.hourEntries.reduce((sum, entry) => sum + Number(entry?.hours || 0), 0)
        : 0;
      const hoursFromLegacyRows = Array.isArray(workorder?.urenRegels)
        ? workorder.urenRegels.reduce((sum, entry) => sum + Number(entry?.uren || 0), 0)
        : 0;
      const registeredHours = hoursFromEntries > 0 ? hoursFromEntries : hoursFromLegacyRows;

      acc.registeredHours += Number(registeredHours || 0);
      acc.revenueExcl += Number(workorder?.financialTotals?.subtotalExcl || 0);
      acc.materialExcl += Number(workorder?.financialTotals?.materialsSubtotal || 0);
      return acc;
    },
    {
      registeredHours: 0,
      revenueExcl: 0,
      materialExcl: 0,
    }
  );
}
